import { Grid } from '@material-ui/core';
import { Reply } from '@material-ui/icons';
import React, { useEffect } from 'react';
import BaseButton from 'src/common/components/base-button';
import { ICustomer } from 'src/common/interfaces/dtos/ICustomer';
import { IPagedData } from 'src/common/interfaces/dtos/IPagedData';
import { disableScanner } from 'src/devices/scanner-operations';
import Pagination from 'src/pagination/pagination';
import TenderGroupTitle from 'src/tendering/components/tender-group-title';
import VerticalCenter from 'src/vertical-center/vertical-center';
import CustomerDisplayItem from './customer-display-item';
import { styles } from './customer-loyalty-search-results-styles';
import CustomerTableHeader from './customer-loyalty-table-headers';
import EmeraldDialog from 'src/common/components/emerald-dialog/emerald-dialog';
import { useTranslation } from 'react-i18next';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import DoubleTrainingModeWatermark from 'src/common/training-mode/double-training-mode-watermark';

const SearchCustomerResults: React.FC<ISearchCustomerProps> = ({
  onClose,
  onSubmit,
  onBack,
  updateCurrentPageHandler,
  customerDisplayData,
  currentPage,
  trainingModeActive,
}): JSX.Element => {
  const classes = styles();
  const { showPhoneNumberOnCustomerSearch, showStatusOnCustomerSearch } = getCurrentXLRConfig();
  const [selectedCustomer, setSelectedCustomer] = React.useState<ICustomer>();
  const { t } = useTranslation();
  const { showCustomerSearchTotals } = getCurrentXLRConfig();
  useEffect(() => {
    disableScanner();
  }, []);

  const onSelect = (customer: ICustomer): void => {
    if (customer.isActive) {
      if (!selectedCustomer) {
        setSelectedCustomer(customer);
      } else {
        if (selectedCustomer.consumerAccountNumber === customer.consumerAccountNumber) {
          setSelectedCustomer(undefined);
        } else {
          setSelectedCustomer(customer);
        }
      }
    }
  };

  return (
    <EmeraldDialog
      open
      classes={{
        paper: trainingModeActive ? classes.trainingModeBackground : classes.dialogPaper,
      }}
      disablePortal={true}
    >
      {trainingModeActive && <DoubleTrainingModeWatermark />}
      <Grid container direction='column' className={`${classes.mainContent}`}>
        {showCustomerSearchTotals && <TenderGroupTitle title={t('CustomerSearchResultsTitle')} />}
        {!showCustomerSearchTotals && (
          <TenderGroupTitle
            title={t('CustomerSearchResultsTitle', customerDisplayData.totalResults.toString())}
          />
        )}
        <Grid container>
          <BaseButton
            buttonType='white'
            startIcon={<Reply />}
            className={classes.searchCustomerBackButton}
            classes={{ startIcon: classes.searchCustomerBackIcon }}
            data-testid='customer-search-results-button-back'
            onClick={onBack}
          >
            {t('Back')}
          </BaseButton>
        </Grid>
        <Grid container>
          <span className={classes.subContentTitle}>{t('LoyaltySelectAccount')}</span>
        </Grid>
        <Grid container className={classes.customerLoyaltyHeaderRow}>
          <CustomerTableHeader
            showPhoneNumberOnCustomerSearch={showPhoneNumberOnCustomerSearch}
            showStatusOnCustomerSearch={showStatusOnCustomerSearch}
          />
        </Grid>
        <Grid
          container
          className={classes.customerSearchResultsList}
          data-testid='customer-search-results-list'
        >
          {customerDisplayData &&
            customerDisplayData.pageContent.map(
              (customer, i): JSX.Element => {
                return (
                  <CustomerDisplayItem
                    customer={customer}
                    key={customer.consumerAccountNumber || `customer${i}`}
                    onSelect={onSelect}
                    selected={
                      selectedCustomer
                        ? selectedCustomer.consumerAccountNumber === customer.consumerAccountNumber
                        : false
                    }
                    showPhoneNumberOnCustomerSearch={showPhoneNumberOnCustomerSearch}
                    showStatusOnCustomerSearch={showStatusOnCustomerSearch}
                  />
                );
              },
            )}
        </Grid>
        <Grid container>
          <Grid item xs={4} container justifyContent='flex-start' className='fullHeight'>
            <Pagination
              displayText={!showCustomerSearchTotals}
              currentPage={currentPage}
              totalPages={customerDisplayData.totalPages}
              updateCurrentPageHandler={updateCurrentPageHandler}
              testIdPrefix={'customer-search-results'}
            />
          </Grid>
          <Grid item xs={4} container justifyContent='center'>
            <Grid item>
              <VerticalCenter>
                <BaseButton
                  buttonType='white'
                  className={classes.closeButton}
                  onClick={onClose}
                  data-testid='customer-search-results-button-close'
                >
                  {t('Cancel')}
                </BaseButton>
              </VerticalCenter>
            </Grid>
            <Grid item className={classes.addCustomerItem}>
              <VerticalCenter>
                <BaseButton
                  className={classes.addCustomerButton}
                  disabled={!selectedCustomer}
                  onClick={() => onSubmit(selectedCustomer as ICustomer)}
                  data-testid='customer-search-results-button-accept'
                >
                  {t('AddCustomer')}
                </BaseButton>
              </VerticalCenter>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </EmeraldDialog>
  );
};

export interface ISearchCustomerProps {
  onClose: () => void;
  onSubmit: (customer: ICustomer) => void;
  onBack: () => void;
  updateCurrentPageHandler: (currentPage: number) => void;
  customerDisplayData: IPagedData<ICustomer>;
  currentPage: number;
  trainingModeActive: boolean;
}
export default React.memo(SearchCustomerResults);
